import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchPurchaseOrder = createAsyncThunk(
  "po/fetchPurchaseOrder",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    search_type,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    search_type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (type === "tag" || search_type === "tag") {
        params.append("type", "tag");
        params.append("search_type", "tag");
      }
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
        ...(type === "tag" || search_type === "tag"
          ? { type: "tag", search_type: "tag" }
          : {}),
      };

      const response: any = await Axios.post(
        `${
          API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.LIST
        }?${params.toString()}`,
        body
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Purchase Order List");
    }
  }
);

export const exportPurchaseOrderCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${
      API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.EXPORT
    }?${params.toString()}`,
    body,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `purchase_order_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchPurchaseOrderId = createAsyncThunk(
  "po/fetchPurchaseOrderId",
  async (po_id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.DETAIL(po_id)}`
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch purchase Order by ID"
      );
    }
  }
);

export const fetchPurchaseOrderDropdown = createAsyncThunk(
  "po/fetchPurchaseOrderDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.DROPDOWN}`
      );
      return response?.data?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch purchase Request Dropdown"
      );
    }
  }
);

export const updatePurchaseOrder = createAsyncThunk(
  "po/updatePurchaseOrder",
  async ({ formData, po_id }: any, { rejectWithValue }) => {
    try {
      // If formData is FormData, set proper headers
      const config =
        formData instanceof FormData
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : {};

      const response: any = await Axios.put(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.UPDATE(po_id)}`,
        formData,
        config
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update purchase Request"
      );
    }
  }
);

export const updateStatusPurchaseOrder = createAsyncThunk(
  "po/updateStatusPurchaseOrder",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.STATUS_UPDATE(
          id
        )}`
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update PR status"
      );
    }
  }
);

export const addPurchaseOrder = createAsyncThunk(
  "po/addPurchaseOrder",
  async (data: any, { rejectWithValue }) => {
    try {
      // If data is FormData, set proper headers
      const config =
        data instanceof FormData
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : {};

      const response: any = await Axios.post(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.CREATE}`,
        data,
        config
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create PO");
    }
  }
);

export const revertPurchaseOrder = createAsyncThunk(
  "po/revertPurchaseOrder",
  async (po_id: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.REVERT_PURCHASE_ORDER(
          po_id
        )}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to revert Purchase Order"
      );
    }
  }
);

export const fetchPOAnalytics = createAsyncThunk(
  "po/fetchPOAnalytics",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.PO_ANALYTICS}`
      );
      return response?.data?.data || response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch PO analytics"
      );
    }
  }
);

export const downloadPurchaseOrderPdf = createAsyncThunk(
  "quotation/downloadProductQuotationPdf",
  async (
    { id, poNumber }: { id: string; poNumber: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.DOWNLOAD_PDF(id),
        {
          responseType: "blob", // Important for handling binary file data
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Purchase_Order_${poNumber}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download PDF");
    }
  }
);
